<template>
  <div class="el-main">
    <ShokurekishoTextareaCard
      :current-card-id="currentCardId"
      card-id="skills-to-use"
      title="スキル（保有技術）"
      lead="応募先にアピールできるスキル（技能）や資格、経験などを入力しましょう"
      field="skills"
      :autosize="true"
      input-title="スキル（保有技術）"
    >
      <span style="color: red"
        >※「スキル（保有技術）」の入力は1行までとなります。2行になりますとフォーマットが崩れます。</span
      >
      <div
        class="attention-limit character"
        v-if="(formatData.skills || '').length >= 35"
      >
        <i class="el-icon-warning"></i>
        目安の文字数をオーバーしています。文章を短くしましょう。
      </div>
    </ShokurekishoTextareaCard>
    <ShokurekishoTextareaCard
      :current-card-id="currentCardId"
      card-id="intelligence"
      title="活かせる経験・知識"
      lead="「どのような経験があるのか」と「どのような技術・知識があるのか」を簡単に入力しましょう"
      field="intelligence"
      :autosize="{ minRows: 3, maxRows: 5 }"
    />
    <ShokurekishoTextareaCard
      :current-card-id="currentCardId"
      card-id="other-skills"
      title="その他"
      lead="github等のアピールしたいURLやサービス名などを入力しましょう"
      field="otherskills"
      :autosize="{ minRows: 3, maxRows: 5 }"
    />
    <ShokurekishoSkillsheetJobHistoryCard
      :basic-data="basicData"
      :current-card-id="currentCardId"
    />
    <ShokurekishoPrMessageCard
      :current-card-id="currentCardId"
      :example-sentences="exampleSentences"
    />
    <ShokurekishoCreateDateCard
      :current-card-id="currentCardId"
      heading="スキルシートの作成日を入力"
    />

    <div class="card" v-show="currentCardId == 'confirm'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>作成したスキルシートを確認</h2>
        </div>
      </div>
    </div>

    <div
      class="card"
      v-show="currentCardId == 'output'"
      v-if="getMode != 'production1'"
    >
      <DownloadItem
        :formatId="formatId"
        :isSendYagiPostEnabled="isSendYagiPostEnabled"
        :isSendYagiEmailEnabled="isSendYagiEmailEnabled"
        :mailPdfEnabled="mailPdfEnabled"
        :downloadPdfEnabled="downloadPdfEnabled"
        :downloadActiveCollapses="downloadActiveCollapses"
        @update:isSendYagiEmailEnabled="isSendYagiEmailEnabled = $event"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useFormatLocalData } from '@/composables/format-edit/useFormatLocalData'
import {
  createEmptyShokurekishoFormatData,
  provideShokurekishoFormatData,
  type ShokurekishoExampleSentences
} from '@/composables/format-edit/useShokurekishoFormatData'
import {
  provideShokurekishoHistories,
  useShokurekishoHistories
} from '@/composables/format-edit/useShokurekishoHistories'
import ShokurekishoTextareaCard from '@/components/format-edit/shokurekisho/ShokurekishoTextareaCard.vue'
import ShokurekishoSkillsheetJobHistoryCard from '@/components/format-edit/shokurekisho/ShokurekishoSkillsheetJobHistoryCard.vue'
import ShokurekishoPrMessageCard from '@/components/format-edit/shokurekisho/ShokurekishoPrMessageCard.vue'
import ShokurekishoCreateDateCard from '@/components/format-edit/shokurekisho/ShokurekishoCreateDateCard.vue'
import DownloadItem from '@/components/formats/CommonParts/DownloadItem.vue'
import type { BasicData } from '@/utils/format-edit/basicData'

defineProps<{
  basicData: BasicData
  formatId?: string
  currentCardId?: string
  exampleSentences?: ShokurekishoExampleSentences
}>()

const { formLib, vm, yagiprimePaymentService } = useHostServices()

const { formatData, saveKey, saveName, changeCount } = useFormatLocalData({
  saveName: 'skillsheet_type',
  saveOtherName: 'rirekisho',
  createEmpty: createEmptyShokurekishoFormatData
})
provideShokurekishoFormatData(formatData)

const histories = useShokurekishoHistories(formatData)
provideShokurekishoHistories(histories)

const mailPdfEnabled = ref(true)
const downloadPdfEnabled = ref(true)
const isSendYagiPostEnabled = ref(true)
const isSendYagiEmailEnabled = ref(true)
const downloadActiveCollapses = ref(['postcard'])

const getMode = computed(() => process.env.VUE_APP_MODE)

function dataChanged() {
  mailPdfEnabled.value = true
  downloadPdfEnabled.value = true
}

type PaymentLifecycle = {
  init: (host: unknown) => void
  cleanup: (host: unknown) => void
}

const paymentService =
  yagiprimePaymentService as typeof yagiprimePaymentService & PaymentLifecycle

onBeforeMount(() => {
  if (formatData.createDate === null) {
    formatData.createDate = formLib.getToday()
  }
  if (!formatData.skillsToUse) formatData.skillsToUse = []
  if (!formatData.jobHistories) formatData.jobHistories = []
  if (typeof formatData.skills !== 'string') formatData.skills = ''
  if (typeof formatData.intelligence !== 'string') formatData.intelligence = ''
  if (typeof formatData.otherskills !== 'string') formatData.otherskills = ''
  paymentService.init(vm)
  emitter.on('basic-data-changed', dataChanged)
  emitter.on('format-data-changed', dataChanged)
})

onBeforeUnmount(() => {
  emitter.off('basic-data-changed', dataChanged)
  emitter.off('format-data-changed', dataChanged)
  paymentService.cleanup(vm)
})

defineExpose({
  saveName,
  saveKey,
  formatData,
  changeCount,
  addSkillHistory: histories.addSkillHistory,
  addJobHistory: histories.addJobHistory
})
</script>

<style lang="scss" scoped>
* {
  list-style: none;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

img {
  width: 100%;
  vertical-align: bottom;
}

.card-header h1,
.card-header h2 {
  padding-bottom: 1.5rem;
}
</style>
